import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { config } from "../../utils/config";
import { getCookie } from "../../utils/cookieHandler";
import SearchableDropdown from "../DynamicList/SearchableDropdown";
import InputDateTimePickerMUI from "../InputFields/InputDatePicker/InputDateTimePickerMUI";
import { findDataInFields } from "../../utils/helperFuctions";

const ContractGenerator = (props) => {
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactId, setContactId] = useState("");
  const [pickupDate, setPickupDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const authHeaders = useMemo(() => {
    const token = getCookie("_AT");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // vehicle and branch
  const vehicleId = props.id;
  const VehicleBranch = findDataInFields(
    props.currentFields,
    "location.branch"
  );

  useEffect(() => {
    let ignore = false;
    const fetchContacts = async () => {
      setLoadingContacts(true);
      setError("");
      try {
        const { data } = await axios.get(
          `${config.apiUrl}/api/contacts?pagination=false&companyRelation=true`,
          {
            headers: authHeaders,
          }
        );
        if (!ignore) {
          const list = Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data)
            ? data
            : [];
          setContacts(list);
        }
      } catch (e) {
        if (!ignore)
          setError(
            e?.response?.data?.error || e.message || "Failed to load contacts"
          );
      } finally {
        if (!ignore) setLoadingContacts(false);
      }
    };
    fetchContacts();
    return () => {
      ignore = true;
    };
  }, [authHeaders]);

  const onGenerate = async () => {
    setError("");
    if (!vehicleId) {
      setError("Missing vehicle ID");
      return;
    }
    if (!contactId) {
      setError("Please select a customer");
      return;
    }
    if (!pickupDate) {
      setError("Please select a pickup date");
      return;
    }

    try {
      setSubmitting(true);
      // 1) get temporary token
      const tokenResp = await axios.get(`${config.apiUrl}/api/auth/pdf-token`, {
        headers: authHeaders,
      });
      const tempToken = tokenResp.data.token;

      // 2) trigger backend generation (similar to TemporaryRta)
      const url = `${
        config.apiUrl
      }/api/vehicles/lendingContract/${vehicleId}?access_token=${tempToken}&contactId=${contactId}&pickupDate=${encodeURIComponent(
        pickupDate
      )}`;
      const genResp = await axios.get(url, { headers: authHeaders });

      // 3) open generated asset
      window.open(`${config.apiUrl}/assets/` + genResp.data.name, "_blank");
    } catch (e) {
      setError(
        e?.response?.data?.error || e.message || "Failed to generate contract"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const contactOptions = useMemo(() => {
    return (contacts || []).map((c) => ({
      value: c.id,
      label: `${c.first_name || c.name || ""} ${c.last_name || ""}${
        c.company ? ` - ${c.company.name}` : ""
      }`.trim(),
    }));
  }, [contacts]);

  return (
    <div className="space-y-3">
      {error ? <div className="text-red-600 text-sm">{error}</div> : null}

      <div className="flex gap-3 items-end flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <label className="block text-sm font-medium text-gray-700">
            Customer
          </label>
          <SearchableDropdown
            options={contactOptions}
            value={contactId}
            onChange={(e) => setContactId(e.value)}
            placeholder="Select customer"
            isLoading={loadingContacts}
            disabled={loadingContacts || submitting}
            fullWidth={true}
          />
        </div>

        <div className="flex-1 min-w-[220px]">
          <InputDateTimePickerMUI
            countryBranchCode={"location.branch.code"}
            allowTime={true}
            label={"Pickup date"}
            name={"pickupDate"}
            defaultValue={pickupDate}
            handleChange={(e) => setPickupDate(e.target.value)}
            isClearable={true}
            wrapperStyle={{ marginTop: 0 }}
            disableInput={submitting}
          />
        </div>

        <div>
          <button
            type="button"
            className="inline-flex items-center justify-center w-[240px] px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60"
            onClick={onGenerate}
            disabled={submitting}
          >
            {submitting ? "Generating..." : "Generate Lending Contract"}
          </button>
        </div>
      </div>

      {!vehicleId ? (
        <div className="text-xs text-gray-500">
          Vehicle ID not detected from context
        </div>
      ) : null}
    </div>
  );
};

export default ContractGenerator;
